/**
 * Read a picked file into a data URL so it can live in React state (in memory
 * only). Never written to localStorage / sessionStorage.
 */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read-failed'));
    reader.readAsDataURL(file);
  });
}

/** Human readable file size for hints / labels. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Best-effort file extension for a remote URL, defaulting to a sensible value. */
function extensionFromUrl(url: string, fallback: string): string {
  try {
    const clean = url.split('?')[0].split('#')[0];
    const match = clean.match(/\.([a-zA-Z0-9]{2,5})$/);
    return match ? match[1].toLowerCase() : fallback;
  } catch {
    return fallback;
  }
}

/** Fallback download that lets the browser open/save the file in a new tab. */
function anchorDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Download a remote file (e.g. an image) by fetching it as a blob and
 * triggering a real browser download. When the fetch is blocked (e.g. by
 * CORS) it falls back to letting the browser open/save the file directly.
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error('download-failed');
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    anchorDownload(url, filename);
  }
}

/** Build a friendly download file name for a receipt tied to an id. */
export function receiptFileName(id: string, url: string): string {
  const safeId = (id || 'receipt').replace(/[^a-zA-Z0-9-_]/g, '-');
  return `transfer-receipt-${safeId}.${extensionFromUrl(url, 'jpg')}`;
}